import React, { useRef, useState } from "react";
import { Download, Maximize2, Minimize2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/shared/components/ui/dialog";
import { BrandButton, IconButton } from "@/shared/components/ui-kit";
import { FileTypeIcon } from "@/shared/components/icons/filetype";

/* The one way anything opens full size — a file, an artifact, a generated image, a picture or
   diagram in an answer: the dialog with the 72px header (type icon, name, one line about it,
   Expand viewer, the action, the close button), the thing itself on the sunk page below.
   `ViewerDialog` fills it with file and artifact previews; this frame carries none of their
   libraries, so the extension's answers open in it too. */

type Props = {
  title: string;
  /** the type icon's kind: a file extension such as "png" or "docx" */
  icon: string;
  /** the line under the name */
  description: React.ReactNode;
  /** the header's action: Download, or an Export menu */
  action: React.ReactNode;
  /** under the header, over the thing: a version picker, an error */
  toolbar?: React.ReactNode;
  busy?: boolean;
  onClose: () => void;
  children: React.ReactNode;
};

export default function ViewerFrame({ title, icon, description, action, toolbar, busy, onClose, children }: Props) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [expanded, setExpanded] = useState(false);
  return <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
    <DialogContent onOpenAutoFocus={event => { event.preventDefault(); titleRef.current?.focus(); }} aria-busy={busy} className={`p-0 gap-0 flex flex-col overflow-hidden bg-[var(--canvas)] border-[var(--hairline-strong)] [&_button:disabled]:opacity-40 [&_button:disabled]:pointer-events-none [&>button:last-child]:right-4 [&>button:last-child]:top-[22px] ${expanded ? "w-screen max-w-none h-dvh rounded-none sm:rounded-none" : "w-screen max-w-none h-dvh rounded-none sm:w-[96vw] sm:max-w-[1440px] sm:h-[94dvh] sm:rounded-[16px]"}`}>
      <header className="h-[72px] shrink-0 flex items-center gap-3 pl-4 sm:pl-6 pr-14 border-b hairline bg-[var(--surface)]">
        <div className="hidden sm:flex w-10 h-10 items-center justify-center rounded-[10px] ring-1 ring-[var(--hairline)] bg-[var(--surface-sunk)]">
          <FileTypeIcon kind={icon} size={24} />
        </div>
        <div className="min-w-0 flex-1">
          <DialogTitle ref={titleRef} tabIndex={-1} title={title} className="outline-none text-[15px] font-medium leading-5 tracking-[-0.01em] truncate text-[var(--text-primary)]">{title}</DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--text-secondary)] mt-0.5 truncate">{description}</DialogDescription>
        </div>
        <IconButton className="hidden sm:flex" aria-label={expanded ? "Exit fullscreen" : "Expand viewer"} title={expanded ? "Exit fullscreen" : "Expand viewer"} onClick={() => setExpanded(!expanded)}>{expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}</IconButton>
        <div aria-hidden="true" className="hidden sm:block h-5 w-px bg-[var(--hairline)] mx-1" />
        {action}
      </header>
      {toolbar}
      <div className="flex-1 min-h-0 overflow-hidden bg-[var(--surface-sunk)]">{children}</div>
    </DialogContent>
  </Dialog>;
}

/** The header's Download. */
export function DownloadButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return <BrandButton disabled={disabled} onClick={onClick}><Download size={14} />Download</BrandButton>;
}

/** A picture in the frame: centred, shrunk to fit, never telling its server where it was seen. */
export function ImagePreview({ src, alt }: { src: string; alt: string }) {
  return <div className="p-4 sm:p-8 h-full overflow-auto flex items-center justify-center"><img src={src} alt={alt} referrerPolicy="no-referrer" className="max-w-full max-h-full object-contain" /></div>;
}
